import React, { useState, useEffect } from 'react';
import { Check, Zap, Shield, Crown, Loader2 } from 'lucide-react';
import { getFirestore, collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { initializeApp } from 'firebase/app';
import config from '../config';

// Initialize Firebase (reuse existing app if already initialized)
const app = initializeApp(config.firebaseConfig);
const db = getFirestore(app);

/**
 * Icon mapping: Convert icon names from Firestore to React components
 */
const ICON_MAP = {
    zap: <Zap className="w-5 h-5 text-blue-500" />,
    shield: <Shield className="w-5 h-5 text-purple-500" />,
    crown: <Crown className="w-5 h-5 text-yellow-500" />,
    default: <Shield className="w-5 h-5 text-gray-500" />
};

const getIcon = (iconName) => {
    return ICON_MAP[iconName?.toLowerCase()] || ICON_MAP.default;
};

const Subscription = ({ user, activeSubscription }) => {
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [plans, setPlans] = useState([]);
    const [plansLoading, setPlansLoading] = useState(true);

    // Fetch subscription plans from Firestore
    useEffect(() => {
        const plansQuery = query(
            collection(db, 'subscription_plans'),
            where('active', '==', true),
            orderBy('displayOrder', 'asc')
        );

        const unsubscribe = onSnapshot(plansQuery, (snapshot) => {
            const fetchedPlans = [];
            snapshot.forEach((doc) => {
                const data = doc.data();
                fetchedPlans.push({
                    ...data,
                    icon: getIcon(data.iconName)
                });
            });
            setPlans(fetchedPlans);
            setPlansLoading(false);
        }, (error) => {
            console.error('Error fetching subscription plans:', error);
            setPlansLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Load Razorpay SDK
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);
        return () => {
            document.body.removeChild(script);
        };
    }, []);

    const isSubscribed = !!activeSubscription;
    const currentPlan = isSubscribed ? plans.find(p => p.id === activeSubscription.planId) : null;

    const handleSubscribeNow = (planId) => {
        if (isSubscribed) return;
        if (selectedPlanId === planId) {
            handlePayNow();
        } else {
            setSelectedPlanId(planId);
        }
    };

    const handlePayNow = async () => {
        if (isSubscribed) {
            alert("You already have an active subscription.");
            return;
        }

        if (!selectedPlanId) {
            alert("Please select a plan.");
            return;
        }

        // Check for user
        if (!user) {
            alert("Please sign in to subscribe.");
            return;
        }

        const plan = plans.find(p => p.id === selectedPlanId);
        setLoading(true);

        try {
            // STEP 1: Create Order on Backend
            const backendUrl = config.API_BASE_URL;
            const orderRes = await fetch(`${backendUrl}/api/create-order`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    amount: plan.slashedPrice,
                    planId: plan.id,
                    userEmail: user.email,
                    userId: user.uid
                })
            });

            const order = await orderRes.json();
            if (!order.id) throw new Error("Order creation failed on server.");

            // STEP 2: Open Razorpay Checkout with order_id
            const options = {
                key: 'rzp_live_SA7YMNLPF25BHW',
                amount: order.amount,
                currency: order.currency,
                name: "JanBharatAI",
                description: `Subscription: ${plan.name}`,
                order_id: order.id,
                handler: async function (response) {
                    // STEP 3: Verify Payment on Backend
                    setLoading(true);
                    const verifyRes = await fetch(`${backendUrl}/api/verify-payment`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                            userEmail: user.email,
                            userId: user.uid,
                            planId: plan.id,
                            durationDays: plan.durationDays,
                            amount: order.amount, // Pass amount (in paise)
                            currency: order.currency
                        })
                    });

                    const result = await verifyRes.json();
                    if (result.verified) {
                        // alert("Subscription activated successfully!");
                        // Navigation or state refresh happens automatically via App.js listener

                        // Trigger GA4 purchase event
                        if (window.gtag) {
                            window.gtag("event", "purchase", {
                                transaction_id: response.razorpay_payment_id,
                                value: parseFloat(plan.slashedPrice),
                                currency: "INR",
                                items: [
                                    {
                                        item_id: plan.id,
                                        item_name: plan.name,
                                        price: parseFloat(plan.slashedPrice),
                                        quantity: 1
                                    }
                                ]
                            });
                        }
                    } else {
                        alert("Payment verification failed. Please contact support.");
                    }
                    setLoading(false);
                },
                modal: {
                    ondismiss: function () {
                        setLoading(false);
                    }
                },
                prefill: {
                    name: user.displayName,
                    email: user.email
                },
                theme: {
                    color: "#2563eb"
                }
            };

            const rzp = new window.Razorpay(options);
            rzp.open();
        } catch (error) {
            console.error("Payment E2E error:", error);
            alert("Payment process failed. Ensure the backend is running.");
            setLoading(false);
        }
    };

    // Loading state while fetching plans
    if (plansLoading) {
        return (
            <div className="min-h-[70svh] flex items-center justify-center p-4">
                <div className="text-center space-y-4">
                    <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto" />
                    <p className="text-zinc-600 dark:text-zinc-400">Loading subscription plans...</p>
                </div>
            </div>
        );
    }

    // No plans available
    if (plans.length === 0) {
        return (
            <div className="min-h-[70svh] flex items-center justify-center p-4">
                <div className="text-center space-y-4">
                    <p className="text-zinc-600 dark:text-zinc-400">No subscription plans available at the moment.</p>
                </div>
            </div>
        );
    }

    if (isSubscribed) {
        return (
            <div className="min-h-[70svh] flex items-center justify-center p-4">
                <div className="w-full max-w-4xl p-8 bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 transition-all">
                    <div className="text-center space-y-4">
                        <div className="bg-green-100 dark:bg-green-900/30 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Check className="text-green-600 dark:text-green-400 w-8 h-8" />
                        </div>
                        <h2 className="text-3xl font-bold text-zinc-900 dark:text-white">Premium Active</h2>
                        <p className="text-zinc-500 dark:text-zinc-400">
                            You have full access to <span className="text-zinc-900 dark:text-white font-semibold">JanBharatAI Premium</span>.
                        </p>

                        <div className="p-8 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-3xl border border-blue-100 dark:border-blue-800/50 mt-8">
                            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                                <div className="text-left space-y-2">
                                    <span className="px-3 py-1 bg-blue-600 text-white text-[10px] font-bold rounded-full uppercase tracking-widest">Active Plan</span>
                                    <h3 className="text-2xl font-bold text-zinc-900 dark:text-white">{currentPlan?.name || 'Premium Plan'}</h3>
                                </div>
                                <div className="flex gap-4">
                                    <div className="text-center p-4 bg-white dark:bg-zinc-800/50 rounded-2xl shadow-sm border border-zinc-100 dark:border-zinc-700 min-w-[150px]">
                                        <p className="text-[10px] text-zinc-400 uppercase font-bold mb-1">Expires On</p>
                                        <p className="text-lg font-bold text-zinc-900 dark:text-white">
                                            {activeSubscription.expiresAt?.toDate ? activeSubscription.expiresAt.toDate().toLocaleString() : new Date(activeSubscription.expiresAt).toLocaleString()}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 pt-6 border-t border-zinc-100 dark:border-zinc-800">
                            <p className="text-xs text-zinc-400">
                                Subscriber Email: {user?.email} | ID: {activeSubscription.id}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-6 space-y-12 animate-in fade-in duration-700">
            <div className="text-center space-y-4">
                <h1 className="text-3xl md:text-5xl font-bold text-transparent bg-gradient-to-r from-orange-500 via-stone-500 to-green-600 mb-2 font-display tracking-tight bg-clip-text px-4 animate-in fade-in slide-in-from-top-4 duration-1000">
                    Digital India Growth Plans
                </h1>
                <p className="text-zinc-600 dark:text-zinc-300 text-lg max-w-2xl mx-auto">
                    Special Subsidized Rates
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {plans.map((plan) => (
                    <div
                        key={plan.id}
                        className={`relative flex flex-col p-8 rounded-3xl transition-all duration-300 transform border-2 ${selectedPlanId === plan.id
                            ? 'border-blue-500 bg-blue-50/30 dark:bg-blue-900/10 scale-105 shadow-xl'
                            : 'border-zinc-100 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:border-zinc-200 dark:hover:border-zinc-700'
                            }`}
                    >
                        {plan.popular && (
                            <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg">
                                MOST POPULAR
                            </span>
                        )}

                        <div className="mb-6">
                            <div className="mb-4 inline-block p-3 bg-zinc-100 dark:bg-zinc-800 rounded-2xl">
                                {plan.icon}
                            </div>
                            <h3 className="text-xl font-bold text-zinc-900 dark:text-white">{plan.name}</h3>
                            <p className="text-zinc-500 dark:text-zinc-400 text-sm font-medium">{plan.description}</p>
                        </div>

                        <div className="mb-8">
                            <div className="flex items-baseline gap-2">
                                <span className="text-4xl font-bold text-zinc-900 dark:text-white">₹{plan.slashedPrice}</span>
                                <span className="text-lg text-zinc-400 dark:text-zinc-500 line-through">₹{plan.originalPrice}</span>
                            </div>
                            <p className="text-zinc-400 dark:text-zinc-500 text-xs mt-1 font-medium uppercase tracking-tight">Limited time offer</p>
                        </div>

                        <ul className="space-y-4 mb-8 flex-grow">
                            {plan.features && plan.features.map((feature, idx) => (
                                <li key={idx} className="flex items-center gap-3 text-sm text-zinc-700 dark:text-zinc-200">
                                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" strokeWidth={3} />
                                    <span>{feature}</span>
                                </li>
                            ))}
                        </ul>

                        <button
                            onClick={() => handleSubscribeNow(plan.id)}
                            disabled={loading && selectedPlanId === plan.id}
                            className={`w-full py-4 px-6 rounded-2xl font-bold transition-all flex items-center justify-center gap-2 ${selectedPlanId === plan.id
                                ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30 transform scale-[1.02]'
                                : 'bg-zinc-900 dark:bg-zinc-800 text-white hover:bg-zinc-800'
                                }`}
                        >
                            {loading && selectedPlanId === plan.id ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            ) : selectedPlanId === plan.id ? (
                                <>Pay Now ₹{plan.slashedPrice}</>
                            ) : (
                                'Subscribe Now'
                            )}
                        </button>
                    </div>
                ))}
            </div>

        </div>
    );
};

export default Subscription;
